(ns frontend.view.modals.decisions.event
  (:require
   [clojure.set :as set]
   [common.localization :as loc]
   [common.schema.entities.decision-advisory-board :as advisory-board-decision-schema]
   [common.schema.entities.aifm-decision.schema :as aifm-decision-schema]
   [common.schema.entities.decision-aifm-exit :as aifm-exit-decision-schema]
   [common.schema.entities.decision-condition :as decision-condition-schema]
   [common.schema.entities.ic-decision.schema :as ic-decision-schema]
   [common.schema.entities.decision-ic-exit :as ic-exit-decision-schema]
   [common.schema.entities.decision-participant :as decision-participant-schema]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [frontend.controller.websocket.event :as ws]
   [frontend.global-events.toast :as toast]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [frontend.view.modals.decisions.sub :as sub]
   [potpuri.core :refer [dissoc-in]]
   [re-frame.core :as rf]))

(defn validate-entity-data
  [entities]
  (let [validation-errors
        (->> entities
             (keep
              (fn [entity]
                (let [id (:db/id entity)
                      entity-type (:entity/type entity)
                      required-fields (case entity-type
                                        :entity.type/advisory-board-decision
                                        advisory-board-decision-schema/required-fields
                                        :entity.type/aifm-decision
                                        aifm-decision-schema/required-fields
                                        :entity.type/ic-decision
                                        ic-decision-schema/required-fields
                                        :entity.type/aifm-exit-decision
                                        aifm-exit-decision-schema/required-fields
                                        :entity.type/ic-exit-decision
                                        ic-exit-decision-schema/required-fields
                                        :entity.type/decision-condition
                                        decision-condition-schema/required-fields
                                        :entity.type/decision-participant
                                        decision-participant-schema/required-fields
                                        #{})
                      validation-errors
                      (->> required-fields
                           (keep (fn [field]
                                   (let [value (get entity field)]
                                     (when (or (not value)
                                               (= value "")
                                               (and (set? value) (empty? value)))
                                       [field (loc/tr :required)]))))
                           (into {}))]
                  (when-not (empty? validation-errors)
                    {id validation-errors}))))
             (apply merge))]
    (when-not (empty? validation-errors)
      validation-errors)))

(rf/reg-event-db
 ::open-create-modal
 (fn [db [_ decision-id decision-entity-type containing-entity-id]]
   (let [decision {:db/id                    decision-id
                   :entity/type              decision-entity-type
                   :common/containing-entity containing-entity-id}
         entities {decision-id decision}]
     (-> db
         (assoc ::sub/entities entities)
         (assoc ::sub/decision-id decision-id)
         (assoc ::sub/modal-type sub/create-modal)
         (assoc ::sub/entity-type decision-entity-type)))))

(rf/reg-event-db
 ::open-editor-modal
 (fn [db [_ decision-id]]
   (let [decision (session/get-entity decision-id)
         decision-entity-type (:entity/type decision)
         tenant-schema @session/tenant-schema
         entity-schema (registry/get-schema tenant-schema decision-entity-type)
         associated-entity-ids
         (->> entity-schema
              (filter (fn [[field field-schema]]
                        (let [data-type (:field/data-type field-schema)]
                          (and (= (namespace field) "decision")
                               (some #{data-type}
                                     [:data.type/ref-single
                                      :data.type/ref-multi])))))
              (map (fn [[field _]]
                     (let [value (get decision field)]
                       (if (set? value)
                         value
                         #{value}))))
              (apply concat)
              (remove nil?)
              (set))
         associated-entity-id->entity
         (->> associated-entity-ids
              (reduce (fn [acc entity-id]
                        (assoc acc entity-id (session/get-entity entity-id)))
                      {}))
         entities (merge
                   {decision-id decision}
                   associated-entity-id->entity)]
     (-> db
         (assoc ::sub/entities entities)
         (assoc ::sub/decision-id decision-id)
         (assoc ::sub/modal-type sub/editor-modal)
         (assoc ::sub/entity-type decision-entity-type)))))

(rf/reg-event-db
 ::cleanup
 (fn [db _]
   (dissoc db
           ::sub/modal-type
           ::sub/entity-type
           ::sub/modified?
           ::sub/decision-id
           ::sub/entities
           ::sub/validation-errors
           ::sub/focused-condition-id
           ::sub/added-entity-ids
           ::sub/disabled-entity-ids
           ::sub/delete-confirmation-modal?)))

(rf/reg-event-db
 ::set-delete-confirmation-modal-open
 (fn [db [_]]
   (assoc db ::sub/delete-confirmation-modal? true)))

(rf/reg-event-db
 ::cancel-delete-decision
 (fn [db _]
   (-> db
       (dissoc ::sub/delete-confirmation-modal?))))

(rf/reg-event-fx
 ::delete-decision-success
 (fn [{:keys [db]} [_ close-modal! resp]]
   (close-modal!)
   {:db (dissoc db ::sub/delete-confirmation-modal?)
    :fx [[::toast/success ["Decision deleted"]]]}))

(rf/reg-event-fx
 ::delete-decision-error
 (fn [{:keys [db]} _]
   (let []
     (js/console.log "delete decision error")
     {:db db})))
(rf/reg-event-fx
 ::delete-decision
 (fn [{:keys [db]} [_ close-modal!]]
   (let [decision-id (::sub/decision-id db)]
     {:db       db
      ::ws/send {:id         :decision/disable!
                 :body       {:entity-id decision-id}
                 :on-success [::delete-decision-success close-modal!]
                 :on-error   [::delete-decision-error]}})))

(rf/reg-event-db
 ::update-entity-field
 (fn [db [_ entity-id field value]]
   (-> db
       (assoc-in [::sub/entities entity-id field] value)
       (dissoc-in [::sub/validation-errors entity-id field]))))

(rf/reg-event-db
 ::add-disabled-entity-id
 (fn [db [_ entity-id]]
   (cond-> db
           true (dissoc-in [::sub/entities entity-id])
           true (update ::sub/added-entity-ids set/difference #{entity-id})
           (not (string? entity-id)) (update ::sub/disabled-entity-ids set/union #{entity-id}))))

(rf/reg-event-db
 ::add-added-entity-ids
 (fn [db [_ entity-id-or-ids]]
   (let [value (if (set? entity-id-or-ids)
                 entity-id-or-ids
                 #{entity-id-or-ids})]
     (update db ::sub/added-entity-ids set/union value))))

; Submitting
(rf/reg-event-fx
 ::on-submit-success
 (fn [{:keys [db]} [_ label close-modal! resp]]
   (close-modal!)
   {:db db
    :fx [[::toast/success [label]]]}))

(rf/reg-event-fx
 ::on-submit-error
 (fn [{:keys [db]} [_ label resp]]
   (let []
     (js/console.log "submit error")
     {:db db})))

(rf/reg-event-fx
 ::create-decision
 (fn [{:keys [db]} [_ close-modal!]]
   (let [decision-id (::sub/decision-id db)
         entities (::sub/entities db)
         decision (get entities decision-id)
         validation-errors (validate-entity-data (vals entities))
         body {:decision-id decision-id
               :entities    entities}]
     (if validation-errors
       {:db (assoc db ::sub/validation-errors validation-errors)}
       {:db       (assoc db ::sub/validation-errors nil)
        ::ws/send {:id         :decision/create
                   :body       body
                   :on-success [::on-submit-success "Decision created" close-modal!]
                   :on-error   [::on-submit-error "Decision creation failed"]}}))))

(rf/reg-event-fx
 ::update-decision
 (fn [{:keys [db]} [_ close-modal!]]
   (let [decision-id (::sub/decision-id db)
         entities (::sub/entities db)
         decision (get entities decision-id)
         validation-errors (validate-entity-data (vals entities))
         added-entity-ids (::sub/added-entity-ids db)
         disabled-entity-ids (::sub/disabled-entity-ids db)
         body {:decision-id         decision-id
               :entities            (cu/remove-blank-vals entities)
               :added-entity-ids    added-entity-ids
               :disabled-entity-ids disabled-entity-ids}]
     (if validation-errors
       {:db (assoc db ::sub/validation-errors validation-errors)}
       {:db       (assoc db ::sub/validation-errors nil)
        ::ws/send {:id         :decision/update
                   :body       body
                   :on-success [::on-submit-success "Decision updated" close-modal!]
                   :on-error   [::on-submit-error]}}))))


; Decision participants
(rf/reg-event-fx
 ::add-decision-participants
 (fn [{:keys [db]} [_ account-or-person-ids]]
   (let [decision-id (::sub/decision-id db)
         entities (::sub/entities db)
         decision (get entities decision-id)
         prev-participant-ids (->> decision
                                   :decision/participants
                                   (set))
         prev-participant-person-ids
         (->> prev-participant-ids
              (map (partial get entities))
              (mapv :decision-participant/person))

         added-participants
         (->> account-or-person-ids
              (remove (fn [account-or-person-id]
                        (some #{account-or-person-id} prev-participant-person-ids)))
              (map (fn [account-or-person-id]
                     (let [data {:db/id                           (fu/create-temp-id "temp-participant-")
                                 :entity/type                     :entity.type/decision-participant
                                 :decision-participant/person     account-or-person-id
                                 :decision-participant/attendance :decision-participant.attendance/attended}]
                       data))))
         added-participant-id->entity
         (->> added-participants
              (reduce (fn [acc curr]
                        (assoc acc (:db/id curr) curr))
                      {}))
         added-participant-ids (set (keys added-participant-id->entity))
         decision-participant-ids (set/union prev-participant-ids added-participant-ids)

         new-db (-> db
                    (assoc ::sub/entities (merge entities added-participant-id->entity))
                    (update ::sub/added-entity-ids set/union added-participant-ids))]
     {:db new-db
      :fx [[:dispatch [::update-entity-field decision-id :decision/participants decision-participant-ids]]
           [:dispatch [::add-added-entity-ids added-participant-ids]]]})))

(rf/reg-event-fx
 ::remove-decision-participant
 (fn [{:keys [db]} [_ removed-participant-id]]
   (let [decision-id (::sub/decision-id db)
         entities (::sub/entities db)
         decision (get entities decision-id)
         prev-participant-ids (->> decision
                                   :decision/participants)
         participant-ids (set/difference prev-participant-ids #{removed-participant-id})]
     {:db (assoc-in db [::sub/entities decision-id :decision/participants] participant-ids)
      :fx [[:dispatch [::add-disabled-entity-id removed-participant-id]]]})))


; Investment reservations
(rf/reg-event-fx
 ::add-investment-reservation
 (fn [{:keys [db]} [_ investment-reservation-type-id]]
   (let [decision-id (get db ::sub/decision-id)
         entities (get db ::sub/entities)
         decision (get entities decision-id)
         prev-reservation-ids (:decision/investment-reservations decision)
         decision-containing-entity (-> decision
                                        :common/containing-entity
                                        (session/get-entity))
         investment-reservation-type
         (session/get-entity investment-reservation-type-id)

         enabled-fields
         (:investment-reservation-type/enabled-fields investment-reservation-type)

         reservation-id (fu/create-temp-id "temp-investment-reservation-")
         default-currency (case (:entity/type decision-containing-entity)
                            :entity.type/oppo
                            (or
                             (:oppo/cur-ticket--sym decision-containing-entity)
                             (:tenant/default-currency @session/tenant))

                            (:tenant/default-currency @session/tenant))
         new-reservation
         (merge
          {:db/id                       reservation-id
           :common/ord                  (inc (count prev-reservation-ids))
           :entity/type                 :entity.type/investment-reservation
           :investment-reservation/type investment-reservation-type-id}
          (when (some #{:investment-reservation/amount} enabled-fields)
            {:investment-reservation/amount--sym default-currency}))

         reservation-ids (set/union prev-reservation-ids #{reservation-id})]
     {:db (assoc-in db [::sub/entities reservation-id] new-reservation)
      :fx [[:dispatch [::update-entity-field decision-id :decision/investment-reservations reservation-ids]]
           [:dispatch [::add-added-entity-ids reservation-id]]]})))

(rf/reg-event-fx
 ::remove-investment-reservation
 (fn [{:keys [db]} [_ removed-investment-reservation-id]]
   (let [decision-id (get db ::sub/decision-id)
         entities (get db ::sub/entities)
         decision (get entities decision-id)
         prev-reservation-ids (:decision/investment-reservations decision)
         reservation-ids (set/difference prev-reservation-ids #{removed-investment-reservation-id})]
     {:db (assoc-in db [::sub/entities decision-id :decision/investment-reservations] reservation-ids)
      :fx [[:dispatch [::add-disabled-entity-id removed-investment-reservation-id]]]})))


; Conditions

(rf/reg-event-fx
 ::add-decision-condition
 (fn [{:keys [db]} _]
   (let [decision-id (get db ::sub/decision-id)
         prev-conditions (->> decision-id
                              (get (::sub/entities db))
                              :decision/conditions)

         condition-id (fu/create-temp-id "temp-decision-condition-")
         condition {:db/id       condition-id
                    :entity/type :entity.type/decision-condition
                    :common/ord  (inc (count prev-conditions))}

         decision-conditions (set/union prev-conditions #{condition-id})]
     {:db (assoc-in db [::sub/entities condition-id] condition)
      :fx [[:dispatch [::update-entity-field decision-id :decision/conditions decision-conditions]]
           [:dispatch [::add-added-entity-ids condition-id]]
           [:dispatch [::set-focused-condition-id condition-id]]]})))

(rf/reg-event-fx
 ::remove-decision-condition
 (fn [{:keys [db]} [_ removed-condition-id]]
   (let [decision-id (get db ::sub/decision-id)
         prev-conditions (->> decision-id
                              (get (::sub/entities db))
                              :decision/conditions)
         decision-conditions (set/difference prev-conditions #{removed-condition-id})]
     {:db (assoc-in db [::sub/entities decision-id :decision/conditions] decision-conditions)
      :fx [[:dispatch [::add-disabled-entity-id removed-condition-id]]]})))

(rf/reg-event-db
 ::set-focused-condition-id
 (fn [db [_ condition-id]]
   (assoc db ::sub/focused-condition-id condition-id)))
